/**
 * Highlight panels - Figma `6377:87333` (Business Travel) and `6377:88106` (WecoAcademy).
 *
 * Rows of a tinted panel and a photograph. A panel carries an optional small label, its
 * heading and its body; a photograph is one picture, or two upright ones side by side.
 *
 * **`shared.panel` gained `image`, and the section gained `heading` and `lead`.** The
 * design alternates panels with photographs, which the original `items[]` of headings
 * and bodies could not hold, and the frame opens with a heading and a lead paragraph
 * the section had nowhere to put. Which tile is which is decided by the content: an
 * item with an image is the photograph, one without is the panel. That also means the
 * editor controls the alternation by ordering the list, rather than the code assuming
 * it.
 *
 * **Two upright photographs are one item with a `secondImage`** (6377:88107, 6377:88114),
 * so a pair stays a pair however the list is reordered. A row whose photograph is a pair
 * gives the panel the wider share - 786 to 560, as the file measures - where a row with
 * one photograph is two equal halves (6377:87340). A grid's columns are shared by every
 * row, so each row is a grid of its own.
 *
 * **The panel's text sits in the middle of it** (2026-09-26, the designer's call and
 * WecoAcademy's layout): a block at most 486 wide, centred both ways - 150 in from either
 * side of the file's 786 panel, and still centred in a narrower one. Its heading is h4,
 * its body body-medium.
 *
 * **Two palettes, one per page in the file**, chosen with `panelColor`: `green` is every
 * panel `Green-100`, sampled from Business Travel (6377:87342); `pastel` is `Green-50` then
 * `Orange-50`, and round again, as WecoAcademy's two panels are (6377:88108, 6377:88117).
 * The turn counts panels only - the photographs between them do not move it on.
 */
import { Eyebrow, SectionShell } from '../primitives';
import type { HighlightPanelListSection, Card, StrapiMedia } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';

const isPair = (item?: Card) => Boolean(item?.image && item.secondImage);
const isPanel = (item?: Card) => Boolean(item && !item.image);

/**
 * One photograph. It stretches to the row, so it does not leave a gap under it beside a
 * panel with more to say, and the picture is laid in with `absolute inset-0`.
 *
 * **The aspect ratio is on an empty spacer inside the figure, never on the figure.** A grid
 * item with `aspect-ratio` *and* `align-self: stretch` whose only content is positioned
 * resolved to 0px high in Safari: the row was sized from the content (nothing) and the
 * stretch won over the ratio, so the whole row - and the section's photographs - vanished,
 * while Chrome used the ratio. The figure now has no ratio of its own, so it stretches like
 * any other grid item, and the spacer, an ordinary block with an `aspect-ratio`, gives it
 * the height to start from. Found on /pl/nasza-firma, 2026-11-04.
 */
function Photo({ media, alt, aspect, sizes }: { media: StrapiMedia; alt: string; aspect: string; sizes: string }) {
  return (
    <figure className="relative m-0 self-stretch overflow-hidden rounded-s bg-surface-sunken">
      <div aria-hidden="true" className={aspect} />
      <Image media={media} alt={alt} sizes={sizes} className="absolute inset-0 h-full w-full object-cover" />
    </figure>
  );
}

const PALETTES: Record<string, string[]> = {
  green: ['bg-green-100'],
  pastel: ['bg-green-50', 'bg-orange-50']
};

function Tile({ item, panelClass }: { item: Card; panelClass: string }) {
  if (item.image) {
    const alt = (media: StrapiMedia) => media.alternativeText ?? item.title ?? '';
    if (item.secondImage) {
      // Each 273x446 in the file: two upright photographs sharing the tile's width.
      return (
        <div className="grid grid-cols-2 gap-4 self-stretch">
          {[item.image, item.secondImage].map((media, i) => (
            <Photo key={i} media={media} alt={alt(media)} aspect="aspect-[273/446]" sizes={SIZES.quarter} />
          ))}
        </div>
      );
    }
    return <Photo media={item.image} alt={alt(item.image)} aspect="aspect-[16/11]" sizes={SIZES.half} />;
  }

  return (
    <div className={`flex min-h-[380px] items-center rounded-s p-8 ${panelClass}`}>
      <div className="mx-auto flex w-full max-w-[486px] flex-col gap-8">
        {item.label || item.title ? (
          <div className="flex flex-col gap-4">
            {item.label ? <p className="text-body text-ink-muted">{item.label}</p> : null}
            {item.title ? <h3 className="text-h4 text-ink-strong">{item.title}</h3> : null}
          </div>
        ) : null}
        {item.body ? <p className="text-body-md text-ink-muted">{item.body}</p> : null}
      </div>
    </div>
  );
}

/** Two equal halves, unless a pair of photographs sits beside a panel: then 560 to 786. */
function rowColumns([a, b]: Card[]): string {
  if (isPanel(a) && isPair(b)) return 'lg:grid-cols-[786fr_560fr]';
  if (isPair(a) && isPanel(b)) return 'lg:grid-cols-[560fr_786fr]';
  return 'lg:grid-cols-2';
}

export default function HighlightPanelList({ data }: { data: HighlightPanelListSection }) {
  const items = data.items ?? [];
  // Left to right, two to a row - the order in the CMS is the order on the page.
  const rows: Card[][] = [];
  for (let i = 0; i < items.length; i += 2) rows.push(items.slice(i, i + 2));
  const palette = PALETTES[data.panelColor ?? 'green'] ?? PALETTES.green;
  // Each panel's colour, by its place among the panels.
  let panels = 0;
  const colours = items.map((item) => (item.image ? '' : palette[panels++ % palette.length]));

  return (
    <SectionShell settings={data.settings} name="highlight-panel-list">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {data.heading ? <h2 className="max-w-4xl text-h3 text-ink lg:text-h2">{data.heading}</h2> : null}
      {/* Body-medium over the file's 640 (6377:87338). */}
      {data.lead ? <p className="max-w-[640px] whitespace-pre-line text-body-md text-ink-muted">{data.lead}</p> : null}

      {rows.length ? (
        <div className="mt-4 flex flex-col gap-4">
          {rows.map((row, r) => (
            <div key={r} className={`grid gap-4 ${rowColumns(row)}`}>
              {row.map((item, i) => <Tile key={i} item={item} panelClass={colours[r * 2 + i]} />)}
            </div>
          ))}
        </div>
      ) : null}
    </SectionShell>
  );
}
